import React from "react";
import {
  Box,
  Typography,
  useTheme,
  type SxProps,
  type Theme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface BackButtonProps {
  onClick: () => void;
  /** Overrides, e.g. `position: "static"` to sit in a row instead of the corner. */
  sx?: SxProps<Theme>;
}

const BackButton: React.FC<BackButtonProps> = ({ onClick, sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={[
        {
          position: "absolute",
          top: { xs: 12, md: 16 },
          left: { xs: 12, md: 16 },
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          border: "none",
          background: "none",
          font: "inherit",
          color: isDark ? "#9ca3af" : "#64748b",
          borderRadius: 1,
          px: 1,
          py: 0.5,
          WebkitTapHighlightColor: "transparent",
          transition: "all 200ms linear",
          "@media (hover: hover)": {
            "&:hover": {
              color: isDark ? "#e4e6ea" : "#0f172a",
              backgroundColor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.04)",
            },
          },
          "@media (pointer: coarse)": { minHeight: 44 },
          "&:focus-visible": {
            outline: "2px solid #22c55e",
            outlineOffset: "2px",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
      <Typography
        component="span"
        sx={{
          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          fontWeight: 500,
        }}
      >
        Back
      </Typography>
    </Box>
  );
};

export default BackButton;
